import { useCallback, useState } from "react";
import { requestApi } from "~/api/requestApi";
import { scopelyId } from "~/services/scopely/scopelyId";
import { useAuthStore } from "~/stores/auth";
import {
  loginAnalyticsTracking,
  TRACKING_ACTIONS,
  TRACKING_ERROR_CODES,
  TRACKING_TYPES,
  trackSysConnect,
} from "../services/scopely/loginAnalyticsTracking";
import { updateAnalyticsUser } from "../services/scopely/titanAnalytics";
import type { TUserSessionApi } from "../stores/type/TUserSessionApi";
import { SSO_TYPE } from "../types/sso.type";
import {AuthorizationUri} from "@scopely/web-sdk-scopelyid/dist/src/uri";
import { LoginIdentityOperation} from "@scopely/web-sdk-scopelyid";

interface UseAuthOptions {
  onSuccess?: (response: unknown) => void;
  onError?: (error: Error) => void;
}

export const useAuth = ({ onSuccess, onError }: UseAuthOptions = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const setUser = useAuthStore((state) => state.setUser);

  // const ssoLogout = async () => {
  //     const sso_type = localStorage.getItem('login_type') as unknown as SsoType;
  //     try {
  //         await requestApi({
  //             endpoint: `me/logout/${SsoType[sso_type]}`,
  //             method: 'delete',
  //         });
  //         // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  //     } catch (error: any) {
  //         loginAnalyticsTracking({
  //             type: Number(sso_type),
  //             action: TRACKING_ACTIONS.LOG_OUT,
  //             errorCode: `${error?.code || TRACKING_ERROR_CODES.UNKNOWN_ERROR}`,
  //         });
  //     }
  // };

  const ssoLogin = useCallback(
    async (ssoToken: string, ssoType: number) => {
      setIsLoading(true);

      try {
        const scopelyResponse: TUserSessionApi = await requestApi({
          endpoint: "me/sessions",
          method: "post",
          body: {
            ssoToken,
            ssoType,
          },
        });
        if (scopelyResponse?.IsSuccess) {
          localStorage.setItem("sessionToken", scopelyResponse?.Data?.SecretKey);
          localStorage.setItem("loginDate", new Date().toISOString());
          setType(ssoType);
          setUser(scopelyResponse?.Data);
          onSuccess?.(scopelyResponse?.Data);
          updateAnalyticsUser(scopelyResponse?.Data.Id, scopelyResponse?.Data.Email);
          return scopelyResponse?.Data;
        }

        throw new Error("SSO authentication failed");
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      } catch (error: any) {
        onError?.(error as Error);
        loginAnalyticsTracking({
          type: TRACKING_TYPES.SCOPELY_ID,
          action: TRACKING_ACTIONS.LOGIN_FAILED,
          errorCode: `${error?.code || TRACKING_ERROR_CODES.UNKNOWN_ERROR}`,
        });
        trackSysConnect({
          type: "ScopelyId",
          errorCode: `${error?.code || TRACKING_ERROR_CODES.UNKNOWN_ERROR}`,
        })
        throw error;
      } finally {
        setIsLoading(false);
        localStorage.removeItem("authorizing");
        localStorage.removeItem("loginProcessing");
      }
    },
    [setUser, onSuccess, onError],
  );

  const setType = (type: number) => {
    const types =
      Object.keys(SSO_TYPE).find((k) => SSO_TYPE[k as keyof typeof SSO_TYPE] === type) ||
      "FAST_LOGIN";
    localStorage.setItem("login_type", types);
  };

  const generateScopelyToken = useCallback(async () => {
    try {
      const scopelyIdInitialized = await scopelyId;
      const currentLocation = window.location.href;
      if (scopelyIdInitialized && scopelyIdInitialized.isAuthorizeUri(currentLocation)) {
        const authUri = AuthorizationUri.parse(currentLocation);
        const operation = scopelyIdInitialized.getOperationFromState(authUri?.state as string);

        // Complete authorization
        const result = await scopelyIdInitialized.authorizationResult(
          currentLocation,
          operation as LoginIdentityOperation
        );

        if (result.isSuccessful()) {
          const response = result.getResponse();
          return response?.getAccessToken();
        } else {
          console.error("Authorization failed:", result.getError());
          onError?.(new Error(result.getError()));
        }
      }
    } catch (error) {
      onError?.(error as Error);
    }
  }, [onError]);

  return {
    ssoLogin,
    generateScopelyToken,
    isLoading,
  };
};
